import { api, apiErrorMessage } from '@ddone/api-client';
import { useAuth } from '@ddone/auth';
import type { TimerState } from '@ddone/shared-types';
import { Button, Card, ConfirmDialog, useToast } from '@ddone/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CircleStop, Clock3, Pause, Play, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { formatDuration } from '../lib/format';
import { syncDesktopTimer } from '../desktop/tauri-bridge';

export const timerQueryKey = ['timer', 'active'] as const;

export function useActiveTimer() {
  const { hasPermission, session } = useAuth();
  const enabled =
    session.modules.some((module) => module.id === 'time-tracking' && module.enabled) &&
    hasPermission('time.entry.read');
  return useQuery({
    queryKey: timerQueryKey,
    queryFn: async () =>
      normalizeTimer((await api.request<WireTimer | TimerState | null>('/timer')) ?? null),
    enabled,
    refetchInterval: 30_000
  });
}

export function useElapsedSeconds(timer: TimerState | null | undefined) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!timer || timer.status !== 'RUNNING') return;
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [timer]);
  if (!timer) return 0;
  const running =
    timer.status === 'RUNNING'
      ? Math.max(0, Math.floor((now - new Date(timer.startedAt).getTime()) / 1000))
      : 0;
  return timer.accumulatedSeconds + running;
}

export function ActiveTimerPill() {
  const timer = useActiveTimer();
  const elapsed = useElapsedSeconds(timer.data);
  if (!timer.data) return null;
  return (
    <Link to="/time" className="dd-timer-pill">
      <span className="dd-pulse" />
      <Clock3 size={15} />
      <strong>{formatDuration(elapsed)}</strong>
      <span>{timer.data.description ?? timer.data.task?.displayLabel ?? 'Timer attivo'}</span>
    </Link>
  );
}

export function TimerControl({ compact = false }: { compact?: boolean }) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('time.entry.manage');
  const timer = useActiveTimer();
  const elapsed = useElapsedSeconds(timer.data);
  const client = useQueryClient();
  const { notify } = useToast();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const action = useMutation({
    mutationFn: async ({
      operation,
      body
    }: {
      operation: 'start' | 'pause' | 'resume' | 'stop' | 'cancel';
      body?: unknown;
    }) => {
      if (operation === 'cancel') {
        await api.request('/timer', { method: 'DELETE' });
        return null;
      }
      const result = await api.request<WireTimer | TimerState | null>(`/timer/${operation}`, {
        method: 'POST',
        body:
          operation === 'start'
            ? (body ?? { description: 'Timer manuale', billable: false })
            : (body ?? {})
      });
      return operation === 'stop' ? null : normalizeTimer(result);
    },
    onSuccess: async (value) => {
      client.setQueryData(timerQueryKey, value);
      await syncDesktopTimer(value);
      await client.invalidateQueries({ queryKey: ['time-entries'] });
    },
    onError: (error) =>
      notify('Operazione timer non riuscita', {
        description: apiErrorMessage(error),
        variant: 'error'
      })
  });
  if (timer.isError) return <p className="dd-inline-error">{apiErrorMessage(timer.error)}</p>;
  if (!canManage && !timer.data) return null;
  if (!timer.data)
    return (
      <Button
        onClick={() => action.mutate({ operation: 'start' })}
        disabled={!canManage || action.isPending}
      >
        <Play size={16} /> Avvia timer
      </Button>
    );
  return (
    <>
      <Card className={compact ? 'dd-timer-control dd-timer-control--compact' : 'dd-timer-control'}>
        <span className="dd-pulse" />
        <div>
          <small>{timer.data.status === 'PAUSED' ? 'In pausa' : 'Timer attivo'}</small>
          <strong>{formatDuration(elapsed)}</strong>
          {!compact ? (
            <span>
              {timer.data.description ?? timer.data.task?.displayLabel ?? 'Senza descrizione'}
            </span>
          ) : null}
        </div>
        {timer.data.status === 'RUNNING' ? (
          <Button
            variant="secondary"
            size="icon"
            aria-label="Metti in pausa"
            disabled={!canManage}
            onClick={() => action.mutate({ operation: 'pause' })}
          >
            <Pause />
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="icon"
            aria-label="Riprendi"
            disabled={!canManage}
            onClick={() => action.mutate({ operation: 'resume' })}
          >
            <Play />
          </Button>
        )}
        <Button
          variant="primary"
          size="icon"
          aria-label="Ferma timer e salva la registrazione"
          disabled={!canManage}
          onClick={() => action.mutate({ operation: 'stop' })}
        >
          <CircleStop />
        </Button>
        {!compact ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Scarta timer senza salvare"
            disabled={!canManage}
            onClick={() => setConfirmCancel(true)}
          >
            <Trash2 />
          </Button>
        ) : null}
      </Card>
      <ConfirmDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Scartare il timer?"
        description="Il timer corrente verrà eliminato senza creare una registrazione di tempo."
        confirmLabel="Scarta timer"
        busy={action.isPending}
        onConfirm={() =>
          action.mutate({ operation: 'cancel' }, { onSuccess: () => setConfirmCancel(false) })
        }
      />
    </>
  );
}

export interface WireTimer {
  id: string;
  version: number;
  state: 'RUNNING' | 'PAUSED';
  startedAt: string;
  pausedAt?: string;
  pauseSeconds: number;
  description?: string;
  taskId?: string;
  projectId?: string;
  clientId?: string;
  billable: boolean;
}

export function normalizeTimer(timer: WireTimer | TimerState | null): TimerState | null {
  if (!timer || 'status' in timer) return timer;
  const accumulatedSeconds =
    timer.state === 'PAUSED' && timer.pausedAt
      ? Math.max(
          0,
          Math.floor(
            (new Date(timer.pausedAt).getTime() - new Date(timer.startedAt).getTime()) / 1000
          ) - timer.pauseSeconds
        )
      : -timer.pauseSeconds;
  return {
    id: timer.id,
    version: timer.version,
    status: timer.state,
    startedAt: timer.startedAt,
    ...(timer.pausedAt ? { pausedAt: timer.pausedAt } : {}),
    accumulatedSeconds,
    ...(timer.description ? { description: timer.description } : {}),
    ...(timer.taskId
      ? {
          task: {
            moduleId: 'tasks',
            entityType: 'task',
            entityId: timer.taskId,
            displayLabel: timer.description ?? timer.taskId
          }
        }
      : {}),
    ...(timer.projectId
      ? {
          project: {
            moduleId: 'projects',
            entityType: 'project',
            entityId: timer.projectId,
            displayLabel: timer.projectId
          }
        }
      : {}),
    ...(timer.clientId
      ? {
          client: {
            moduleId: 'clients',
            entityType: 'client',
            entityId: timer.clientId,
            displayLabel: timer.clientId
          }
        }
      : {}),
    billable: timer.billable
  };
}
